// import { QUICK_DEPLOY_STEPS } from '@/pages/Cluster/ClusterList/CreateClusterModal';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import {
  checkDisasterRecoveryCluster,
  getAllClusterEnvs,
  getAllClusterSize,
  getAllInspectionRules,
  getClusterAdminList,
  getClusterDetailByName,
  getClusterList,
  getGeoList,
  getInspectionRuleExecuteResults,
  getRecoveryClusterCheckInfo,
  getRunningLog,
  getUnhealthResultDetail,
  handleErrorTopicService,
} from '@/services/clusterService';
import { isEmpty } from '@/utils/common';
import { CLUSTER_TYPES } from '@/utils/constant';

// 执行步骤
export const QUICK_DEPLOY_STEPS = Object.freeze({
  SELECT_CLUSTER_TYPE: 'SELECT_CLUSTER_TYPE',
  CHOICE_CLUSTER: 'CHOICE_CLUSTER',
  MAINTAIN_HOSTS: 'MAINTAIN_HOSTS',
  MAINTAIN_K8S: 'MAINTAIN_K8S',
});

export const TOPIC_HANDLE_STATUS = Object.freeze({
  UNHANDLED: 'UNHANDLED',
  HANDLED: 'HANDLED',
  HANDLING: 'HANDLING',
  ERROR: 'ERROR',
});

const initLogDetail = {
  readNum: 0,
  startLine: 0,
  total: 0,
  text: '',
  fromNum: 0,
  loading: false,
};

const initialState = {
  loading: false,
  detailLoading: false,
  basic: {},
  detail: {},
  list: [],
  chartLoading: false,
  chartsData: {}, // 图表数据
  excuteResultList: [],
  excuteResultListFetching: false,
  rules: [],
  listBackup: [],
  allList: [],
  clusterEnvs: [],
  filterOptions: [
    { value: 'clusterTypeOrder', label: '集群类型' },
    { value: 'time', label: '接入时间' },
    { value: 'messageSize', label: '消息总大小' },
    { value: 'bytesIn', label: '生产吞吐' },
    { value: 'bytesOut', label: '消费吞吐' },
  ],
  orderOptions: [
    { value: 'desc', label: '降序' },
    { value: 'asc', label: '升序' },
  ],
  unhealthResult: {},
  fetchingUnHealth: false,
  quickDeploy: {
    currentStep: QUICK_DEPLOY_STEPS.SELECT_CLUSTER_TYPE,
    clusterType: CLUSTER_TYPES.HOST, // 主机
    clusterInfo: {},
  },
  fetchingGeoList: false,
  geoList: [],
  logDetail: initLogDetail,
  bindRecovery: {
    currentStep: 1,
    checking: false,
    topicNum: 0,
    topics: [],
    errorTopics: [],
    errorTopicsNum: 0,
    handledTopicsNum: 0,
    canRetry: false,
    handleDone: false,
  }, // 绑定备集群逻辑
  historyOpen: false,
  adminOptions: [],
};

export const fetchBasicAsync = createAsyncThunk('cluster/fetchClusterSize', async () => {
  const response = await getAllClusterSize();
  return response.data;
});

export const fetchClusterAdminList = createAsyncThunk('cluster/fetchClusterAdminList', async () => {
  const response = await getClusterAdminList();
  return response.data;
});

// 获取健康度状态结果
export const fetchInspectionRuleExecuteResults = createAsyncThunk(
  'cluster/fetchInspectionRuleExecuteResults',
  async (params) => {
    const response = await getInspectionRuleExecuteResults(params);
    return response.data;
  },
);

// 获取所有健康度配置
export const fetchAllInspectionRules = createAsyncThunk(
  'cluster/fetchAllInspectionRules',
  async (clusterType) => {
    const response = await getAllInspectionRules(clusterType);
    return response.data;
  },
);

// 集群列表
export const fetchClusterList = createAsyncThunk(
  'cluster/fetchClusterList',
  async (params = {}) => {
    const { sortKey, sortType, ...others } = params;
    const response = await getClusterList(others);
    return (response.data || []).map((item) => {
      let clusterTypeOrder = 2; // 默认权重
      if (item.isDisasterRecoveryCluster) {
        clusterTypeOrder = 1; // 备集群权重
      }
      if (item.enableDisasterRecovery) {
        clusterTypeOrder = 3; // 主集群权重
      }
      if (item.clusterStatus !== 'live') {
        clusterTypeOrder = 0; // 离线
      }

      return {
        ...item,
        clusterTypeOrder,
      };
    });
  },
);

export const fetchAllClusterEnvs = createAsyncThunk('cluster/fetchAllClusterEnvs', async () => {
  const { data = [] } = await getAllClusterEnvs();

  return data.map((item) => ({
    ...item,
    label: item.envName,
    value: item.envKey,
  }));
});

// 获取集群详情
export const fetchClusterDetailByName = createAsyncThunk(
  'cluster/fetchClusterDetailByName',
  async (name) => {
    const response = await getClusterDetailByName(name);
    return response.data;
  },
);

// 异常健康度查询
export const fetchUnhealthResultDetail = createAsyncThunk(
  'cluster/fetchUnhealthResultDetail',
  async (name) => {
    const response = await getUnhealthResultDetail(name);
    return response.data;
  },
);

export const fetchGeoList = createAsyncThunk('cluster/fetchGeoList', async (clusterName) => {
  const response = await getGeoList(clusterName);
  return response.data;
});

export const fetchRunningLog = createAsyncThunk('cluster/fetchRunningLog', async (params) => {
  const response = await getRunningLog(params);
  return response.data;
});

export const fetchInitialRunningLog = createAsyncThunk(
  'cluster/fetchInitialRunningLog',
  async (params) => {
    const response = await getRunningLog(params);
    return response.data;
  },
);

// 集群列表 - 所有， 并选中默认
export const fetchAllClusterList = createAsyncThunk(
  'cluster/fetchAllClusterList',
  async (defaultName) => {
    const response = await getClusterList();
    let hasSelect = false;
    const result = (response.data || []).map((item) => {
      item.selected = false;
      if (item.clusterName === defaultName) {
        item.selected = true;
        hasSelect = true;
      }
      return item;
    });
    // 再次尝试使用第一个
    if (!hasSelect && result && result.length) {
      result[0].selected = true;
    }
    return result;
  },
);

export const disasterRecoveryClusterCheck = createAsyncThunk(
  'cluster/disasterRecoveryClusterCheck',
  async (params) => {
    const response = await checkDisasterRecoveryCluster(params);
    return response.data;
  },
);

export const fetchRecoveryClusterCheckInfo = createAsyncThunk(
  'cluster/fetchRecoveryClusterCheckInfo',
  async (params) => {
    const response = await getRecoveryClusterCheckInfo(params);
    return response.data;
  },
);

export const handleErrorTopic = createAsyncThunk(
  'cluster/handleErrorTopic',
  async (params, api) => {
    const { topicName } = params.results[0];
    try {
      api.dispatch(markHanding(topicName));
      const response = await handleErrorTopicService(params);
      const { meta } = response;

      if (meta.code === 70414) {
        const errorInfo = (meta.publicMessage || '').split(' : ')[1] || '';
        console.log(errorInfo);
        const [matchKey, topic] = errorInfo.match(/\{(.*?)\}/);
        return api.rejectWithValue({
          topicName,
          errorTopic: topic, // Topic来源，批量操作备用
          errorMsg: errorInfo.replace(matchKey, ''),
        });
      }

      return { topicName };
    } catch (error) {
      console.trace(error);
      throw error;
    }
  },
);

export const clusterSlice = createSlice({
  name: 'cluster',
  initialState,
  reducers: {
    resetAllList: (state) => {
      state.allList = [];
    },
    toggleHistoryOpen: (state) => {
      state.historyOpen = !state.historyOpen;
    },
    doSort: (state, action) => {
      const { sortKey, sortType } = action.payload;
      if (isEmpty(sortKey) || isEmpty(sortType)) {
        state.list = state.listBackup; // 恢复
        return;
      }
      state.list = state.list.sort((prev, next) => {
        let prevVal = prev[sortKey] || 0;
        let nextVal = next[sortKey] || 0;
        if (sortKey === 'time') {
          prevVal = dayjs(prev.createTime).unix();
          nextVal = dayjs(next.createTime).unix();
        }
        if (sortType === 'desc') {
          return nextVal - prevVal;
        }
        return prevVal - nextVal;
      });
    },
    selectCuster: (state, action) => {
      const name = action.payload;
      if (name) {
        state.allList = state.allList.map((item) => {
          item.selected = false;
          if (item.clusterName === name) {
            item.selected = true;
          }
          return item;
        });
      }
    },
    setQuickDeployClusterType: (state, action) => {
      const type = action.payload;
      state.quickDeploy.clusterType = type;
    },
    resetQuickDeploy: (state) => {
      state.quickDeploy = initialState.quickDeploy;
    },
    setQuickDeployStep: (state, action) => {
      const step = action.payload;
      state.quickDeploy.currentStep = step;
    },
    setQuickDeployClusterInfo: (state, action) => {
      state.quickDeploy.clusterInfo = action.payload;
    },
    setBindRecoveryStep: (state, action) => {
      const step = action.payload;
      state.bindRecovery.currentStep = step;
    },
    markHanding: (state, action) => {
      const topicName = action.payload;
      const handlingTopic = state.bindRecovery.errorTopics.find(
        (item) => item.topicName === topicName,
      );
      handlingTopic.handled = TOPIC_HANDLE_STATUS.HANDLING;
    },
    resetClusterDetail: (state) => {
      state.detail = {};
    },
    resetBindRecovery: (state) => {
      state.bindRecovery = initialState.bindRecovery;
    },
    resetLogState: (state) => {
      state.logDetail = initLogDetail;
    },
    resetFromNum: (state, action) => {
      state.logDetail.fromNum = action.payload;
      state.logDetail.text = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBasicAsync.fulfilled, (state, action) => {
        state.basic = action.payload;
      })
      .addCase(fetchClusterList.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchClusterList.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload;
        state.listBackup = action.payload;
      })
      .addCase(fetchClusterList.rejected, (state) => {
        state.loading = false;
        state.list = [];
      })
      .addCase(fetchAllClusterList.fulfilled, (state, action) => {
        state.allList = action.payload;
      })
      .addCase(fetchClusterDetailByName.pending, (state) => {
        state.detailLoading = true;
        state.detail = {};
      })
      .addCase(fetchClusterDetailByName.fulfilled, (state, action) => {
        state.detail = action.payload;
        state.detailLoading = false;
      })
      .addCase(fetchClusterDetailByName.rejected, (state) => {
        state.detailLoading = false;
      })
      .addCase(fetchInspectionRuleExecuteResults.pending, (state) => {
        state.excuteResultListFetching = true;
        state.excuteResultList = [];
      })
      .addCase(fetchInspectionRuleExecuteResults.fulfilled, (state, action) => {
        state.excuteResultList = action.payload;
        state.excuteResultListFetching = false;
      })
      .addCase(fetchInspectionRuleExecuteResults.rejected, (state) => {
        state.excuteResultListFetching = false;
      })
      .addCase(fetchAllInspectionRules.fulfilled, (state, action) => {
        state.rules = action.payload;
      })
      .addCase(fetchUnhealthResultDetail.pending, (state) => {
        state.unhealthResult = {};
        state.fetchingUnHealth = true;
      })
      .addCase(fetchUnhealthResultDetail.fulfilled, (state, action) => {
        state.unhealthResult = action.payload;
        state.fetchingUnHealth = false;
      })
      .addCase(fetchUnhealthResultDetail.rejected, (state) => {
        state.fetchingUnHealth = false;
      })
      .addCase(fetchGeoList.pending, (state) => {
        state.fetchingGeoList = true;
      })
      .addCase(fetchGeoList.fulfilled, (state, action) => {
        state.fetchingGeoList = false;
        state.geoList = action.payload;
      })
      .addCase(fetchGeoList.rejected, (state) => {
        state.fetchingGeoList = false;
      })
      .addCase(fetchAllClusterEnvs.fulfilled, (state, action) => {
        state.clusterEnvs = action.payload;
      })
      // 均从 index: 1 开始计数
      .addCase(fetchInitialRunningLog.fulfilled, (state, action) => {
        const { totalNum } = action.payload;
        const startLine = totalNum > 1000 ? totalNum - 999 : 1;

        // 下次轮训开始使用
        state.logDetail.startLine = startLine;
        state.logDetail.readNum = totalNum - startLine + 1;
        state.logDetail.total = totalNum;
        state.logDetail.fromNum = startLine; // 缓存初始读取行数
      })
      .addCase(fetchRunningLog.pending, (state) => {
        state.logDetail.loading = true;
      })
      .addCase(fetchRunningLog.rejected, (state) => {
        state.logDetail.loading = false;
      })
      .addCase(fetchRunningLog.fulfilled, (state, action) => {
        const { totalNum, logContent = '', toLineNum } = action.payload;
        const { text } = state.logDetail;
        state.logDetail.total = totalNum;
        state.logDetail.loading = false;
        if (logContent) {
          const txt = text + logContent;
          state.logDetail.text = txt;
        }
        // 如果有新数据，下次继续读。
        // 否则仅轮询
        if (totalNum - toLineNum > 0) {
          state.logDetail.startLine = toLineNum + 1;
          state.logDetail.readNum = totalNum - toLineNum;
        } else {
          state.logDetail.startLine = toLineNum;
          state.logDetail.readNum = 0;
        }
      })
      .addCase(disasterRecoveryClusterCheck.pending, (state) => {
        state.bindRecovery.checking = true;
      })
      .addCase(fetchRecoveryClusterCheckInfo.fulfilled, (state, action) => {
        const result = action.payload;
        if (result) {
          // 如果为空，说明还在准备，不作处理，等待下个周期
          const { topicNum, process = [], status } = action.payload;

          state.bindRecovery.topics = process;
          state.bindRecovery.topicNum = topicNum;
          const errorTopics = process.filter(
            (item) =>
              item.existed === false ||
              item.consistentPartitions === false ||
              item.consistentReplicas === false ||
              item.consistentPermissions === false,
          );
          state.bindRecovery.errorTopics = errorTopics.map((item) => ({
            ...item,
            handled: TOPIC_HANDLE_STATUS.UNHANDLED, // 标记为未处理，备用
          }));
          state.bindRecovery.errorTopicsNum = errorTopics.length;

          if (status === 'end') {
            state.bindRecovery.checking = false;
          }
        }
      })

      .addCase(handleErrorTopic.pending, (state) => {
        state.bindRecovery.canRetry = false;
      })
      .addCase(handleErrorTopic.fulfilled, (state, action) => {
        const { topicName } = action.payload;
        const errorTopicsSize = state.bindRecovery.errorTopics.length;
        const topicIndex = state.bindRecovery.errorTopics.findIndex(
          (item) => item.topicName === topicName,
        );
        const handlingTopic = state.bindRecovery.errorTopics[topicIndex];
        // 标记处理成功
        handlingTopic.handled = TOPIC_HANDLE_STATUS.HANDLED;
        handlingTopic.existed = true;
        handlingTopic.consistentPartitions = true;
        handlingTopic.consistentReplicas = true;
        handlingTopic.consistentPermissions = true;

        state.bindRecovery.handledTopicsNum = topicIndex + 1;

        if (state.bindRecovery.handledTopicsNum === errorTopicsSize) {
          state.bindRecovery.handleDone = true; // 标记完成
        }
      })

      .addCase(handleErrorTopic.rejected, (state, action) => {
        if (action.payload) {
          const { topicName, errorMsg } = action.payload;
          const handlingTopic = state.bindRecovery.errorTopics.find(
            (item) => item.topicName === topicName,
          );

          handlingTopic.handled = TOPIC_HANDLE_STATUS.ERROR;
          handlingTopic.errorMsg = errorMsg;
        } else {
          // 其他异常
          state.bindRecovery.errorTopics.find(
            (item) => item.handled === TOPIC_HANDLE_STATUS.HANDLING,
          ).handled = TOPIC_HANDLE_STATUS.ERROR;
        }

        state.bindRecovery.canRetry = true;
      })
      .addCase(fetchClusterAdminList.fulfilled, (state, action) => {
        state.adminOptions = action.payload.map((item) => ({
          label: item,
          value: item,
        }));
      });
  },
});

export const {
  doSort,
  selectCuster,
  setQuickDeployClusterType,
  resetQuickDeploy,
  setQuickDeployStep,
  setBindRecoveryStep,
  setQuickDeployClusterInfo,
  resetClusterDetail,
  resetLogState,
  resetFromNum,
  resetAllList,
  resetBindRecovery,
  markHanding,
  toggleHistoryOpen,
} = clusterSlice.actions;
export default clusterSlice.reducer;
